import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  LogOut,
  ChefHat,
  Clock,
  Package,
  Bike,
  CheckCircle,
  ClipboardList,
} from "lucide-react";
import { motion } from "framer-motion";

export default function KitchenPanel() {
  const [token, setToken] = useState(
    sessionStorage.getItem("icp_kitchen_token") || ""
  );
  const [password, setPassword] = useState("");
  const [orders, setOrders] = useState([]);
  const [cateringJobs, setCateringJobs] = useState([]);

  const [kitchenName, setKitchenName] = useState(
    sessionStorage.getItem("icp_kitchen_name") || ""
  );

  const [shopName, setShopName] = useState(
    sessionStorage.getItem("icp_shop_name") || ""
  );

  /* =========================================================
     LOAD NORMAL ORDERS
  ========================================================= */
  const refreshOrders = useCallback(async () => {
    try {
      const response = await api.get("/orders");

      setOrders(
        Array.isArray(response.data)
          ? response.data
          : []
      );
    } catch (error) {
      console.error(
        "Failed to load kitchen orders:",
        error
      );
    }
  }, []);

  /* =========================================================
     LOAD CATERING JOBS
  ========================================================= */
  const refreshCatering = useCallback(async () => {
    try {
      const response = await api.get(
        "/kitchen/catering"
      );

      setCateringJobs(
        Array.isArray(response.data)
          ? response.data
          : []
      );
    } catch (error) {
      console.error(
        "Failed to load catering jobs:",
        error
      );

      // Do not show an error toast every 5 seconds.
    }
  }, []);

  /* =========================================================
     REFRESH EVERYTHING
  ========================================================= */
  const refreshAll = useCallback(async () => {
    await Promise.all([
      refreshOrders(),
      refreshCatering(),
    ]);
  }, [refreshOrders, refreshCatering]);

  /* =========================================================
     AUTO REFRESH
     
     refreshAll is included in dependencies.
     This fixes:
     React Hook useEffect has a missing dependency
  ========================================================= */
  useEffect(() => {
    if (!token) 
      return;
    
    refreshAll();

    const interval = setInterval(refreshAll,5000);
    

    return () => clearInterval(interval);
    
  }, [token, refreshAll]);

  /* =========================================================
     KITCHEN LOGIN
  ========================================================= */
  const login = async () => {
    if (!password.trim()) {
      toast.error("Enter kitchen password");
      return;
    }

    try {
      const response = await api.post(
        "/auth/kitchen",
        {
          password,
        }
      );

      sessionStorage.setItem(
        "icp_kitchen_token",
        response.data.token
      );

      sessionStorage.setItem(
        "icp_kitchen_id",
        response.data.kitchen_id
      );

      sessionStorage.setItem(
        "icp_kitchen_name",
        response.data.kitchen_name
      );

      sessionStorage.setItem(
        "icp_shop_name",
        response.data.shop_name
      );

      setKitchenName(
        response.data.kitchen_name
      );

      setShopName(
        response.data.shop_name
      );

      setToken(response.data.token);
      setPassword("");

      toast.success(
        `Welcome to ${response.data.kitchen_name} — ${response.data.shop_name}`
      );
    } catch (error) {
      console.error(
        "Kitchen login error:",
        error
      );

      toast.error(
        error?.response?.data?.detail ||
          "Invalid kitchen password"
      );
    }
  };

  /* =========================================================
     LOGOUT
  ========================================================= */
  const logout = () => {
    sessionStorage.removeItem(
      "icp_kitchen_token"
    );

    sessionStorage.removeItem(
      "icp_kitchen_id"
    );

    sessionStorage.removeItem(
      "icp_kitchen_name"
    );

    sessionStorage.removeItem(
      "icp_shop_name"
    );

    setKitchenName("");
    setShopName("");
    setOrders([]);
    setCateringJobs([]);
    setToken("");
  };

  /* =========================================================
     NORMAL ORDER STATUS
  ========================================================= */
  const setStatus = async (id, status) => {
    try {
      await api.patch(
        `/orders/${id}/status`,
        {
          status,
        }
      );

      toast.success(
        `Set: ${status.replace(/_/g, " ")}`
      );

      await refreshOrders();
    } catch (error) {
      console.error(
        "Order status update error:",
        error
      );

      toast.error(
        error?.response?.data?.detail ||
          "Failed to update order"
      );
    }
  };

  /* =========================================================
     CATERING STATUS
  ========================================================= */
  const setCateringStatus = async (
    id,
    status
  ) => {
    try {
      await api.patch(
        `/kitchen/catering/${id}/status`,
        {
          status,
        }
      );

      toast.success(
        `Catering: ${status.replace(/_/g, " ")}`
      );

      await refreshCatering();
    } catch (error) {
      console.error(
        "Catering status update error:",
        error
      );

      toast.error(
        error?.response?.data?.detail ||
          "Failed to update catering status"
      );
    }
  };

  /* =========================================================
     LOGIN SCREEN
  ========================================================= */
  if (!token) {
    return (
      <div className="max-w-md mx-auto py-24 px-6">
        <div className="glass rounded-3xl p-8">
          <ChefHat className="w-10 h-10 text-[#B8E14A] mb-3" />

          <h1 className="font-heading text-4xl mb-2">
            Kitchen Login
          </h1>

          <p className="text-sm text-[#B7D8D5] mb-6">
            Enter kitchen password
          </p>

          <input
            type="password"
            data-testid="kitchen-password"
            style={{
              color: "#1F5F5C",
            }}
            value={password}
            onChange={(event) =>
              setPassword(event.target.value)
            }
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                login();
              }
            }}
            className="w-full rounded-full border border-[rgba(184,225,74,0.35)] bg-white px-6 py-3 mb-4 outline-none focus:ring-2 focus:ring-[#B8E14A]"
          />

          <button
            data-testid="kitchen-login"
            onClick={login}
            className="btn-primary w-full justify-center"
          >
            Login
          </button>
        </div>
      </div>
    );
  }

  /* =========================================================
     NORMAL ORDER GROUPS
  ========================================================= */
  const groups = {
    New: orders.filter(
      (order) => order.status === "placed"
    ),

    Preparing: orders.filter(
      (order) =>
        order.status === "accepted" ||
        order.status === "preparing"
    ),

    Ready: orders.filter(
      (order) => order.status === "ready"
    ),

    "Out for Delivery": orders.filter(
      (order) =>
        order.order_type === "DELIVERY" &&
        order.status === "out_for_delivery"
    ),

    Completed: orders.filter(
      (order) =>
        order.order_type === "DINE_IN" &&
        order.status === "completed"
    ),
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
      {/* =====================================================
          HEADER
      ===================================================== */}
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#B8E14A]">
            {kitchenName || "Kitchen"}
          </div>

          <h1 className="font-heading text-4xl">
            {shopName || "Live Orders"}
          </h1>
        </div>

        <button
          data-testid="kitchen-logout"
          onClick={logout}
          className="btn-outline text-sm"
        >
          <LogOut className="w-4 h-4" />
          Logout
        </button>
      </div>

      {/* =====================================================
          CATERING JOBS
      ===================================================== */}
      <section className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-heading text-3xl flex items-center gap-2">
              <ClipboardList className="w-7 h-7 text-[#B8E14A]" />

              Catering Preparation
            </h2>

            <p className="text-sm text-[#B7D8D5] mt-1">
              Only catering jobs assigned to this kitchen
              are shown.
            </p>
          </div>

          <span className="px-3 py-1 rounded-full bg-[#B8E14A] text-[#1F5F5C] text-sm">
            {cateringJobs.length} active
          </span>
        </div>

        {cateringJobs.length === 0 ? (
          <div className="glass rounded-3xl p-6 text-sm text-[#B7D8D5]">
            No catering jobs assigned to this kitchen.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {cateringJobs.map((job) => (
              <motion.div
                key={job.id}
                initial={{
                  opacity: 0,
                  y: 6,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                className="glass rounded-3xl p-5"
                data-testid={`kitchen-catering-${job.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-heading text-xl">
                      {job.name}
                    </div>

                    <div className="text-xs text-[#B7D8D5] mt-1">
                      {job.phone}

                      {job.email
                        ? ` · ${job.email}`
                        : ""}
                    </div>
                  </div>

                  <span className="px-2 py-1 rounded-full bg-white/60 border text-xs uppercase">
                    {String(
                      job.status || ""
                    ).replace(/_/g, " ")}
                  </span>
                </div>

                {/* EVENT DETAILS */}
                <div className="grid sm:grid-cols-2 gap-3 mt-4 text-sm">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#B7D8D5]">
                      Event
                    </div>

                    <div className="mt-1">
                      {job.event_type}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#B7D8D5]">
                      Guests
                    </div>

                    <div className="mt-1">
                      {job.guests}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#B7D8D5]">
                      Date
                    </div>

                    <div className="mt-1">
                      {job.event_date}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#B7D8D5]">
                      Time
                    </div>

                    <div className="mt-1">
                      {job.event_time ||
                        "Not specified"}
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <div className="text-xs uppercase tracking-widest text-[#B7D8D5]">
                      Event Location
                    </div>

                    <div className="mt-1">
                      {job.location}
                    </div>

                    {job.pincode && (
                      <div className="text-xs text-[#B7D8D5] mt-1">
                        Pincode: {job.pincode}
                      </div>
                    )}

                    {job.map_link && (
                      <a
                        href={job.map_link}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-block mt-1 text-[#B8E14A] underline text-xs"
                      >
                        Open event location in Maps
                      </a>
                    )}
                  </div>
                </div>

                {/* REQUIREMENTS */}
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="text-xs uppercase tracking-widest text-[#B7D8D5]">
                    Requirements
                  </div>

                  <div className="mt-1 text-sm">
                    {job.requirements ||
                      "No special requirements"}
                  </div>
                </div>

                {/* QUOTATION SUMMARY */}
                {job.quotation && (
                  <div className="mt-4 rounded-2xl bg-white/40 p-3 text-sm">
                    <div className="flex justify-between">
                      <span>Total</span>

                      <strong>
                        ₹
                        {job.quotation
                          .total_amount ?? 0}
                      </strong>
                    </div>

                    <div className="flex justify-between mt-1">
                      <span>
                        Advance paid
                      </span>

                      <strong>
                        ₹
                        {job.quotation
                          .advance_amount ?? 0}
                      </strong>
                    </div>

                    <div className="flex justify-between mt-1">
                      <span>
                        Balance
                      </span>

                      <strong>
                        ₹
                        {job.quotation
                          .balance_amount ?? 0}
                      </strong>
                    </div>
                  </div>
                )}

                {/* CATERING ACTIONS */}
                <div className="flex flex-wrap gap-2 mt-4">
                  {job.status ===
                    "advance_paid" && (
                    <button
                      onClick={() =>
                        setCateringStatus(
                          job.id,
                          "preparing"
                        )
                      }
                      data-testid={`kitchen-catering-prep-${job.id}`}
                      className="text-xs px-3 py-1.5 rounded-full bg-[#B8E14A] text-[#1F5F5C]"
                    >
                      Start Preparing
                    </button>
                  )}

                  {job.status ===
                    "preparing" && (
                    <button
                      onClick={() =>
                        setCateringStatus(
                          job.id,
                          "ready"
                        )
                      }
                      data-testid={`kitchen-catering-ready-${job.id}`}
                      className="text-xs px-3 py-1.5 rounded-full bg-green-600 text-white"
                    >
                      <Package className="inline w-3 h-3 mr-1" />

                      Mark Ready
                    </button>
                  )}

                  {job.status === "ready" &&
                    job.service_type === "Delivery" && (
                      <button
                        onClick={() =>
                          setCateringStatus(
                            job.id,
                            "out_for_delivery"
                          )
                        }
                        data-testid={`kitchen-catering-out-${job.id}`}
                        className="text-xs px-3 py-1.5 rounded-full bg-blue-600 text-white"
                      >
                        <Bike className="inline w-3 h-3 mr-1" />

                        Out for Delivery
                      </button>
                    )}

                  {job.status === "ready" &&
                    job.service_type !== "Delivery" && (
                      <button
                        onClick={() =>
                          setCateringStatus(
                            job.id,
                            "completed"
                          )
                        }
                        data-testid={`kitchen-catering-complete-${job.id}`}
                        className="text-xs px-3 py-1.5 rounded-full bg-green-600 text-white"
                      >
                        <CheckCircle className="inline w-3 h-3 mr-1" />

                        Completed
                      </button>
                    )}

                  {job.status === "out_for_delivery" && (
                    <button
                      onClick={() =>
                        setCateringStatus(
                          job.id,
                          "completed"
                        )
                      }
                      data-testid={`kitchen-catering-complete-${job.id}`}
                      className="text-xs px-3 py-1.5 rounded-full bg-green-600 text-white"
                    >
                      <CheckCircle className="inline w-3 h-3 mr-1" />

                      Completed
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* =====================================================
          NORMAL ORDERS
      ===================================================== */}
      <section>
        <h2 className="font-heading text-3xl mb-4">
          Live Orders
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
          {Object.entries(groups).map(
            ([label, list]) => (
              <div
                key={label}
                className="glass rounded-3xl p-4"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="font-heading text-lg">
                    {label}
                  </div>

                  <span className="bg-[#B8E14A] text-white text-xs rounded-full px-2 py-0.5">
                    {list.length}
                  </span>
                </div>

                <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
                  {list.map((order) => (
                    <motion.div
                      key={order.id}
                      initial={{
                        opacity: 0,
                        y: 6,
                      }}
                      animate={{
                        opacity: 1,
                        y: 0,
                      }}
                      className="bg-white text-[#1F5F5C] rounded-2xl p-3 shadow-sm"
                      data-testid={`kitchen-order-${order.order_code}`}
                    >
                      {/* ORDER HEADER */}
                      <div className="flex items-center justify-between text-xs text-gray-400">
                        <span>
                          {order.order_code}
                        </span>

                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />

                          {timeAgo(
                            order.created_at
                          )}
                        </span>
                      </div>

                      <div className="font-medium mt-1">
                        {order.delivery?.name ||
                          "Customer"}
                      </div>

                      {/* DINE-IN */}
                      {order.order_type ===
                        "DINE_IN" && (
                        <div className="mt-2 rounded-xl bg-[#B8E14A]/10 border border-[#B8E14A]/30 p-2">
                          <div className="text-xs font-semibold">
                            🍨 DINE-IN
                          </div>

                          <div className="text-xs mt-1">
                            🏪{" "}
                            {order.shop_name}
                          </div>

                          <div className="text-xs mt-1 font-semibold">
                            🪑 Table{" "}
                            {order.table_no}
                          </div>

                          <div className="text-xs mt-1">
                            💵{" "}
                            {order.payment_method ===
                            "CASH"
                              ? "Cash"
                              : "UPI / QR"}
                          </div>
                        </div>
                      )}

                      {/* DELIVERY */}
                      {order.order_type !==
                        "DINE_IN" && (
                        <div className="mt-2 text-xs">
                          🚚 DELIVERY
                        </div>
                      )}

                      {/* ITEMS */}
                      <div className="text-xs text-[#1F5F5C] mt-2 space-y-1">
                        {order.items?.map(
                          (item, index) => (
                            <div key={index}>
                              <div className="font-medium">
                                • {item.name} ×{" "}
                                {item.quantity}
                              </div>

                              {item.options
                                ?.size && (
                                <div className="pl-3 text-[10px]">
                                  {
                                    item
                                      .options
                                      .size
                                  }{" "}
                                  ·{" "}
                                  {
                                    item
                                      .options
                                      .cone
                                  }
                                </div>
                              )}

                              {item.options
                                ?.flavours && (
                                <div className="pl-3 text-[10px] line-clamp-2">
                                  Flavs:{" "}
                                  {item
                                    .options
                                    .flavours
                                    .join(
                                      ", "
                                    )}
                                </div>
                              )}

                              {item.options
                                ?.addons
                                ?.length >
                                0 && (
                                <div className="pl-3 text-[10px] line-clamp-2">
                                  +{" "}
                                  {item
                                    .options
                                    .addons
                                    .join(
                                      ", "
                                    )}
                                </div>
                              )}
                            </div>
                          )
                        )}
                      </div>

                      {/* ORDER ACTIONS */}
                      <div className="flex flex-wrap gap-1 mt-3">
                        {order.status ===
                          "placed" && (
                          <>
                            <button
                              onClick={() =>
                                setStatus(
                                  order.id,
                                  "accepted"
                                )
                              }
                              data-testid={`kitchen-accept-${order.order_code}`}
                              className="text-xs px-3 py-1 rounded-full bg-[#B8E14A] text-[#1F5F5C]"
                            >
                              Accept
                            </button>

                            <button
                              onClick={() =>
                                setStatus(
                                  order.id,
                                  "rejected"
                                )
                              }
                              className="text-xs px-3 py-1 rounded-full border border-red-300 text-red-600"
                            >
                              Reject
                            </button>
                          </>
                        )}

                        {order.status ===
                          "accepted" && (
                          <button
                            onClick={() =>
                              setStatus(
                                order.id,
                                "preparing"
                              )
                            }
                            data-testid={`kitchen-prep-${order.order_code}`}
                            className="text-xs px-3 py-1 rounded-full bg-[#B8E14A] text-white"
                          >
                            Preparing
                          </button>
                        )}

                        {order.status ===
                          "preparing" && (
                          <button
                            onClick={() =>
                              setStatus(
                                order.id,
                                "ready"
                              )
                            }
                            data-testid={`kitchen-ready-${order.order_code}`}
                            className="text-xs px-3 py-1 rounded-full bg-green-600 text-white"
                          >
                            <Package className="inline w-3 h-3 mr-1" />

                            Ready
                          </button>
                        )}

                        {order.status === "ready" &&
                          order.order_type ===
                            "DINE_IN" && (
                            <button
                              onClick={() =>
                                setStatus(
                                  order.id,
                                  "completed"
                                )
                              }
                              data-testid={`kitchen-complete-${order.order_code}`}
                              className="text-xs px-3 py-1 rounded-full bg-green-600 text-white"
                            >
                              <CheckCircle className="inline w-3 h-3 mr-1" />

                              Completed
                            </button>
                          )}

                        {order.status === "ready" &&
                          order.order_type ===
                            "DELIVERY" && (
                            <button
                              onClick={() =>
                                setStatus(
                                  order.id,
                                  "out_for_delivery"
                                )
                              }
                              data-testid={`kitchen-out-${order.order_code}`}
                              className="text-xs px-3 py-1 rounded-full bg-blue-600 text-white"
                            >
                              <Bike className="inline w-3 h-3 mr-1" />

                              Out for Delivery
                            </button>
                          )}
                      </div>
                    </motion.div>
                  ))}

                  {list.length === 0 && (
                    <div className="text-[#B7D8D5] text-xs text-center py-6">
                      No orders
                    </div>
                  )}
                </div>
              </div>
            )
          )}
        </div>
      </section>
    </div>
  );
}

/* =========================================================
   TIME AGO
========================================================= */
function timeAgo(iso) {
  const seconds = Math.floor(
    (Date.now() -
      new Date(iso).getTime()) /
      1000
  );

  if (seconds < 60) {
    return `${Math.max(
      0,
      seconds
    )}s ago`;
  }

  if (seconds < 3600) {
    return `${Math.floor(
      seconds / 60
    )}m ago`;
  }

  return `${Math.floor(
    seconds / 3600
  )}h ago`;
}